import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ActivityIndicator,
  Modal,
  Pressable,
  RefreshControl,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";
import { DateTimePickerAndroid } from "@react-native-community/datetimepicker";
import {
  endOfMonth,
  endOfQuarter,
  endOfWeek,
  endOfYear,
  format,
  parse,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  startOfYear,
} from "date-fns";
import { RouteProp, useRoute } from "@react-navigation/native";

import { DivineBackground } from "../../../shared/ui/DivineBackground";
import { Header } from "../../../shared/ui/Header";
import { ConfirmDialog } from "../../../shared/ui/ConfirmDialog";
import { Button, Input } from "../../../shared/ui/FormElements";
import { Flash } from "../../../shared/utils/Flash";
import { useAuth } from "../../../../context/AuthContext";
import { TimeBasedService } from "../../../services/TimeBasedService";
import { AuthStackParamList } from "../../../navigation/navigationTypes";
import { resolveHistoryRange } from "../../../shared/utils/historyNavigation";
import { getMinutesValidationError, parseValidMinutes } from "../../../shared/utils/minutesValidation";

type FilterType = "Weekly" | "Monthly" | "Quarterly" | "Yearly" | "Custom";
type DeityWorshipHistoryRouteProp = RouteProp<AuthStackParamList, "DeityWorshipHistory">;

type EnumItem = {
  enumerationId: string;
  value: string;
  parameter?: string | number | null;
};

type HistoryRow = {
  date: string; // dd-MM-yyyy
  data: {
    worshipId: string;
    userId: string;
    minutes: any;
    marks: number;
    worshipType: string;
    isEditable: boolean;
  };
};

type WorshipDetail = {
  worshipId: string;
  minutes: any;
  date: string; // ISO or Date string from API
  marks: number;
};

/* ---------- TIME HELPERS ---------- */

const timeOnlyToMinutes = (value: any) => {
  if (!value) return 0;

  if (typeof value === "number") return value;

  if (typeof value === "string") {
    const parts = value.split(":");
    const h = parseInt(parts[0] || "0", 10);
    const m = parseInt(parts[1] || "0", 10);
    return h * 60 + m;
  }

  return 0;
};

const minutesToTimeOnly = (mins: number) => {
  const h = Math.floor(mins / 60);
  const m = mins % 60;

  const hh = String(h).padStart(2, "0");
  const mm = String(m).padStart(2, "0");

  return `${hh}:${mm}:00`;
};

export function DeityWorshipHistoryScreen() {
  const route = useRoute<DeityWorshipHistoryRouteProp>();
  const { user } = useAuth();
  const today = useMemo(() => new Date(), []);
  const initialRange = resolveHistoryRange(route.params, today);

  const [filterType, setFilterType] = useState<FilterType>(
    initialRange.hasNavigationDates ? "Custom" : "Monthly"
  );

  const effectiveUserId = useMemo(() => {
    return initialRange.devoteeId || user?.userId || "";
  }, [initialRange.devoteeId, user?.userId]);

  const [fromDate, setFromDate] = useState(initialRange.fromDate);
  const [toDate, setToDate] = useState(initialRange.toDate);

  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const [worshipTypeId, setWorshipTypeId] = useState<string | null>(null);
  const [parameter, setParameter] = useState<number>(0.2);

  const [entries, setEntries] = useState<HistoryRow[]>([]);
  const [summary, setSummary] = useState({ isSelf: true });

  // Edit modal
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingDateLabel, setEditingDateLabel] = useState<string>("");
  const [editMinutes, setEditMinutes] = useState<string>("0");
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const editMinutesError = useMemo(() => getMinutesValidationError(editMinutes), [editMinutes]);

  const calcMarks = useCallback(
    (mins: number) => Number((mins * (parameter || 0)).toFixed(2)),
    [parameter]
  );

  const minutesPerMarkText = useMemo(() => {
    if (!parameter || parameter <= 0) return "5 Minutes = 1 Mark";
    const m = Math.round(1 / parameter);
    return `${m} Minutes = 1 Mark`;
  }, [parameter]);

  const loadEnumerationAndSetDeity = useCallback(async () => {
    try {
      setLoading(true);

      const res = await TimeBasedService.getEnumeration("WorshipType");
      if (res?.status !== 200) {
        Flash.error(res?.message || "Failed to load worship types");
        return;
      }

      const list = (res?.data || []) as EnumItem[];
      const deity = list.find(
        (x) => (x.value || "").trim().toLowerCase() === "deity"
      );

      if (!deity?.enumerationId) {
        Flash.error("Deity worship type not found in Enumeration");
        return;
      }

      setWorshipTypeId(deity.enumerationId);

      const pRaw = deity.parameter;
      const p =
        typeof pRaw === "number"
          ? pRaw
          : typeof pRaw === "string"
            ? parseFloat(pRaw)
            : 0;

      setParameter(Number.isFinite(p) && p > 0 ? p : 0.2);
    } catch (e: any) {
      Flash.error(e?.message || "Enumeration load error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEnumerationAndSetDeity();
  }, [loadEnumerationAndSetDeity]);

  const applyFilter = (type: FilterType) => {
    setFilterType(type);
    if (type === "Custom") return;

    const now = new Date();
    let start = startOfMonth(now);
    let end = endOfMonth(now);

    if (type === "Weekly") {
      start = startOfWeek(now);
      end = endOfWeek(now);
    } else if (type === "Quarterly") {
      start = startOfQuarter(now);
      end = endOfQuarter(now);
    } else if (type === "Yearly") {
      start = startOfYear(now);
      end = endOfYear(now);
    }

    setFromDate(format(start, "yyyy-MM-dd"));
    setToDate(format(end, "yyyy-MM-dd"));
  };

  const openPicker = (mode: "from" | "to") => {
    const value = new Date(mode === "from" ? fromDate : toDate);
    DateTimePickerAndroid.open({
      value,
      mode: "date",
      onChange: (event, selectedDate) => {
        if (event.type !== "set" || !selectedDate) return;
        const v = format(selectedDate, "yyyy-MM-dd");
        if (mode === "from") setFromDate(v);
        else setToDate(v);
        setFilterType("Custom");
      },
    });
  };

  const loadHistory = useCallback(async () => {
    if (!user?.userId) return;
    if (!worshipTypeId) return;

    try {
      if (!effectiveUserId) return;

      setLoading(true);

      // API expects FromDate/ToDate AND WorshipType
      const payload = {
        userId: effectiveUserId,
        fromDate,
        toDate,
        worshipType: worshipTypeId,
      };

      // ✅ implement this method in your service if not present:
      // POST /Worships/getWorshipHistory
      const res = await TimeBasedService.getWorshipHistory(payload);
      
      if (res?.status !== 200) {
        Flash.error(res?.message || "Failed to load history");
        setEntries([]);
        return;
      }

      const list = (res?.data.records || []) as HistoryRow[];
      setSummary(res.data.response);

      const normalized = list.map((x) => ({
        ...x,
        data: {
          ...x.data,
          minutes: timeOnlyToMinutes(x.data.minutes),
        },
      }));

      setEntries(normalized);
    } catch (e: any) {
      Flash.error(e?.message || "History load error");
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, user?.userId, worshipTypeId]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const onRefresh = async () => {
    setRefreshing(true);
    try {
      await loadHistory();
    } finally {
      setRefreshing(false);
    }
  };

  const confirmDelete = (worshipId: string) => {
    setPendingDeleteId(worshipId);
  };

  const deleteEntry = async (worshipId: string) => {
    try {
      setLoading(true);

      // ✅ implement this method in your service if not present:
      // GET /Worships/delete?worshipId=
      const res = await TimeBasedService.delete(worshipId);

      if (res?.status !== 200) {
        Flash.error(res?.message || "Failed to delete");
        return;
      }

      Flash.success("Success!", "Deity Worship Deleted Successfully!");
      await loadHistory();
    } catch (e: any) {
      Flash.error(e?.message || "Delete failed");
    } finally {
      setLoading(false);
    }
  };

  const openEdit = async (worshipId: string, dateLabel: string) => {
    try {
      setLoading(true);

      // ✅ implement this method in your service if not present:
      // GET /Worships/getWorship?worshipId=
      const res = await TimeBasedService.getWorship(worshipId);

      if (res?.status !== 200) {
        Flash.error(res?.message || "Failed to load entry");
        return;
      }

      const arr = (res?.data || []) as WorshipDetail[];
      const first = arr?.[0];

      if (!first?.worshipId) {
        Flash.error("No entry found");
        return;
      }

      setEditingId(first.worshipId);
      setEditingDateLabel(dateLabel);
      const mins = timeOnlyToMinutes(first.minutes);
      setEditMinutes(String(mins));
    } catch (e: any) {
      Flash.error(e?.message || "Edit load error");
    } finally {
      setLoading(false);
    }
  };

  const saveEdit = async () => {
    if (!editingId) return;
    if (!user?.userId) {
      Flash.error("User not found");
      return;
    }
    if (!worshipTypeId) {
      Flash.error("Worship type not ready");
      return;
    }

    if (editMinutesError) {
      Flash.error(editMinutesError);
      return;
    }
    const mins = parseValidMinutes(editMinutes) ?? 0;
    const m = calcMarks(mins);

    try {
      setLoading(true);

      // Use date from label dd-MM-yyyy -> convert to yyyy-MM-dd for API DateOnly
      const parsed = parse(editingDateLabel, "dd-MM-yyyy", new Date());
      const apiDate = format(parsed, "yyyy-MM-dd");

      const payload = {
        worshipId: editingId,
        userId: user.userId,
        date: apiDate,
        worshipType: worshipTypeId,
        minutes: minutesToTimeOnly(mins),
        marks: m,
      };

      const res = await TimeBasedService.create(payload);
      if (res?.status !== 200) {
        Flash.error(res?.message || "Failed to update");
        return;
      }

      Flash.success("Success!", "Deity Worship Updated Successfully!");
      setEditingId(null);
      await loadHistory();
    } catch (e: any) {
      Flash.error(e?.message || "Update failed");
    } finally {
      setLoading(false);
    }
  };

  const Chip = ({ text, active, onPress }: { text: string; active: boolean; onPress: () => void }) => (
    <Pressable
      onPress={onPress}
      style={({ pressed }) => [
        styles.chip,
        active ? styles.chipActive : styles.chipInactive,
        pressed ? { opacity: 0.95 } : null,
      ]}
    >
      <Text style={[styles.chipText, { color: active ? "#fff" : "#991B1B" }]}>{text}</Text>
    </Pressable>
  );

  return (
    <DivineBackground overlayOpacity={0.95}>
      <Header title="History & Analytics" showBack showHome />

      <ScrollView
        contentContainerStyle={styles.page}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            colors={["#F97316"]}
            tintColor="#F97316"
          />
        }
      >
        {/* Filter */}
        <View style={styles.filterCard}>
          <View style={styles.filterHead}>
            <Text style={styles.filterIcon}>🔎</Text>
            <Text style={styles.filterTitle}>Filter History</Text>
          </View>

          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={{ gap: 8, paddingBottom: 8 }}
          >
            <Chip text="Weekly" active={filterType === "Weekly"} onPress={() => applyFilter("Weekly")} />
            <Chip text="Monthly" active={filterType === "Monthly"} onPress={() => applyFilter("Monthly")} />
            <Chip text="Quarterly" active={filterType === "Quarterly"} onPress={() => applyFilter("Quarterly")} />
            <Chip text="Yearly" active={filterType === "Yearly"} onPress={() => applyFilter("Yearly")} />
          </ScrollView>

          <View style={styles.dateRow}>
            <View style={{ flex: 1 }}>
              <Text style={styles.dateLabel}>From Date</Text>
              <Pressable onPress={() => openPicker("from")} style={styles.dateBox}>
                <Text style={styles.dateBoxIcon}>📅</Text>
                <Text style={styles.dateBoxText}>{format(new Date(fromDate), "dd-MM-yyyy")}</Text>
              </Pressable>
            </View>

            <View style={{ flex: 1 }}>
              <Text style={styles.dateLabel}>To Date</Text>
              <Pressable onPress={() => openPicker("to")} style={styles.dateBox}>
                <Text style={styles.dateBoxIcon}>📅</Text>
                <Text style={styles.dateBoxText}>{format(new Date(toDate), "dd-MM-yyyy")}</Text>
              </Pressable>
            </View>
          </View>
        </View>

        {/* List */}
        {loading && entries.length === 0 ? (
          <View style={styles.emptyBox}>
            <ActivityIndicator />
            <Text style={styles.emptyText}>Loading...</Text>
          </View>
        ) : entries.length === 0 ? (
          <View style={styles.emptyBox}>
            <Text style={styles.emptyText}>No records found for this period.</Text>
          </View>
        ) : (
          <View style={{ gap: 10 }}>
            {entries.map((row, idx) => {
              const d = row.data;
              return (
                <View key={d.worshipId || `${row.date}-${idx}`} style={styles.itemCard}>
                  <View style={styles.itemLeft}>
                    <Text style={styles.itemDate}>
                      {format(parse(row.date, "dd-MM-yyyy", new Date()), "d MMMM yyyy")}
                    </Text>
                    <Text style={styles.itemDay}>
                      {format(parse(row.date, "dd-MM-yyyy", new Date()), "EEEE")}
                    </Text>
                  </View>

                  <View style={styles.itemRight}>
                    <View style={{ alignItems: "flex-end", marginRight: 10 }}>
                      <Text style={styles.itemValue}>{d.minutes}</Text>
                      <Text style={styles.itemLabel}>Minutes</Text>
                    </View>

                    <View style={{ alignItems: "flex-end", marginRight: 14 }}>
                      <Text style={styles.itemValue}>{Number(d.marks).toFixed(1)}</Text>
                      <Text style={styles.itemLabel}>Marks</Text>
                    </View>

                    {summary.isSelf && (
                      row.data.isEditable ? (
                      <Pressable
                        onPress={() => openEdit(d.worshipId, row.date)}
                        style={styles.iconBtn}
                        disabled={loading}
                      >
                        <Text>✏️</Text>
                      </Pressable>
                      ) : (
                        <View style={[styles.iconBtn, styles.iconBtnPlaceholder]} />
                      )
                    )}
                    {summary.isSelf && (
                      <Pressable
                        onPress={() => confirmDelete(d.worshipId)}
                        style={[styles.iconBtn, styles.iconBtnDanger]}
                        disabled={loading}
                      >
                        <Text>🗑️</Text>
                      </Pressable>
                    )}
                  </View>
                </View>
              );
            })}
          </View>
        )}
      </ScrollView>

      {/* Edit Modal */}
      <Modal transparent visible={!!editingId} animationType="fade" onRequestClose={() => setEditingId(null)}>
        <View style={styles.modalOverlay}>
          <View style={styles.modalCard}>
            <View style={styles.modalHead}>
              <Text style={styles.modalTitle}>Edit Entry</Text>
              <Pressable onPress={() => setEditingId(null)} style={styles.modalClose}>
                <Text style={{ fontSize: 16, fontWeight: "900" }}>✕</Text>
              </Pressable>
            </View>

            <Text style={styles.modalDate}>Date: {editingDateLabel}</Text>

            <View style={{ marginTop: 14, gap: 10 }}>
              <Input
                label="Number of Minutes"
                keyboardType="number-pad"
                value={editMinutes}
                onChangeText={(t) => setEditMinutes(t.replace(/[^\d]/g, ""))}
                containerStyle={{ gap: 8 }}
              />
              {editMinutesError ? <Text style={styles.validationText}>{editMinutesError}</Text> : null}
              <Text style={styles.modalHint}>{minutesPerMarkText}</Text>
            </View>

            <View style={styles.modalBtns}>
              <Pressable onPress={() => setEditingId(null)} style={styles.modalCancel} disabled={loading}>
                <Text style={styles.modalCancelText}>Cancel</Text>
              </Pressable>
              <View style={{ flex: 1 }}>
                <Button title={loading ? "Updating..." : "Update"} onPress={saveEdit} disabled={loading || !!editMinutesError} />
              </View>
            </View>

            {loading ? (
              <View style={{ marginTop: 10, alignItems: "center" }}>
                <ActivityIndicator />
              </View>
            ) : null}
          </View>
        </View>
      </Modal>

      <ConfirmDialog
        visible={!!pendingDeleteId}
        title="Delete Entry"
        message="Are you sure you want to delete this entry?"
        confirmText="Delete"
        destructive
        onCancel={() => setPendingDeleteId(null)}
        onConfirm={async () => {
          if (!pendingDeleteId) return;
          await deleteEntry(pendingDeleteId);
          setPendingDeleteId(null);
        }}
      />
    </DivineBackground>
  );
}

const styles = StyleSheet.create({
  page: { padding: 14, paddingBottom: 40, gap: 12 },

  filterCard: {
    backgroundColor: "rgba(255,255,255,0.88)",
    borderRadius: 16,
    padding: 14,
    borderWidth: 1,
    borderColor: "rgba(239,68,68,0.18)",
    shadowColor: "#000",
    shadowOpacity: 0.10,
    shadowRadius: 10,
    shadowOffset: { width: 0, height: 6 },
    elevation: 3,
  },
  filterHead: { flexDirection: "row", alignItems: "center", gap: 8, marginBottom: 10 },
  filterIcon: { fontSize: 16, color: "#EF4444" },
  filterTitle: { fontSize: 14, fontWeight: "900", color: "#1F2937" },

  chip: { paddingHorizontal: 14, paddingVertical: 8, borderRadius: 999 },
  chipActive: {
    backgroundColor: "#EF4444",
    shadowColor: "#000",
    shadowOpacity: 0.12,
    shadowRadius: 6,
    shadowOffset: { width: 0, height: 3 },
    elevation: 2,
  },
  chipInactive: { backgroundColor: "rgba(239,68,68,0.10)" },
  chipText: { fontSize: 12, fontWeight: "900" },

  dateRow: { flexDirection: "row", gap: 12, marginTop: 8 },
  dateLabel: { fontSize: 11, color: "rgba(17,24,39,0.55)", fontWeight: "800", marginLeft: 2, marginBottom: 6 },
  dateBox: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    backgroundColor: "rgba(243,244,246,0.85)",
    borderWidth: 1,
    borderColor: "rgba(0,0,0,0.08)",
    borderRadius: 12,
    paddingHorizontal: 10,
    paddingVertical: 10,
  },
  dateBoxIcon: { fontSize: 14, opacity: 0.6 },
  dateBoxText: { fontSize: 13, fontWeight: "800", color: "rgba(31,41,55,0.90)" },

  emptyBox: {
    backgroundColor: "rgba(255,255,255,0.55)",
    borderRadius: 16,
    paddingVertical: 28,
    alignItems: "center",
    borderWidth: 1,
    borderColor: "rgba(0,0,0,0.06)",
  },
  emptyText: { marginTop: 8, color: "rgba(17,24,39,0.55)", fontWeight: "800" },

  itemCard: {
    backgroundColor: "rgba(255,255,255,0.95)",
    borderRadius: 16,
    padding: 14,
    borderWidth: 1,
    borderColor: "rgba(239,68,68,0.10)",
    shadowColor: "#000",
    shadowOpacity: 0.06,
    shadowRadius: 10,
    shadowOffset: { width: 0, height: 5 },
    elevation: 2,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  itemLeft: { flex: 1 },
  itemDate: { fontSize: 14, fontWeight: "900", color: "#111827" },
  itemDay: { fontSize: 11, marginTop: 4, color: "rgba(17,24,39,0.45)", fontWeight: "800" },

  itemRight: { flexDirection: "row", alignItems: "center" },
  itemValue: { fontSize: 14, fontWeight: "900", color: "#B91C1C" },
  itemLabel: { fontSize: 10, marginTop: 2, color: "rgba(0,0,0,0.35)", fontWeight: "900", textTransform: "uppercase" },

  iconBtn: {
    width: 36,
    height: 36,
    borderRadius: 10,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(239,68,68,0.10)",
    marginLeft: 8,
  },
  iconBtnDanger: { backgroundColor: "rgba(220,38,38,0.10)" },
  iconBtnPlaceholder: { opacity: 0 },
  iconBtnText: { fontSize: 14, fontWeight: "900", color: "#B91C1C" },

  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.35)",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
  },
  modalCard: {
    width: "100%",
    maxWidth: 380,
    backgroundColor: "#fff",
    borderRadius: 18,
    padding: 16,
    shadowColor: "#000",
    shadowOpacity: 0.18,
    shadowRadius: 18,
    shadowOffset: { width: 0, height: 10 },
    elevation: 6,
  },
  modalHead: { flexDirection: "row", justifyContent: "space-between", alignItems: "center" },
  modalTitle: { fontSize: 16, fontWeight: "900", color: "#111827" },
  modalClose: { width: 34, height: 34, borderRadius: 999, alignItems: "center", justifyContent: "center", backgroundColor: "rgba(0,0,0,0.06)" },
  modalDate: { marginTop: 10, fontSize: 12, color: "rgba(0,0,0,0.55)", fontWeight: "700" },
  validationText: { marginTop: 6, fontSize: 11, fontWeight: "800", color: "#DC2626" },
  modalHint: { fontSize: 11, fontWeight: "800", color: "rgba(17,24,39,0.55)" },

  modalBtns: { flexDirection: "row", gap: 10, marginTop: 16 },
  modalCancel: {
    flex: 1,
    height: 46,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: "rgba(0,0,0,0.12)",
    backgroundColor: "rgba(255,255,255,0.95)",
    alignItems: "center",
    justifyContent: "center",
  },
  modalCancelText: { fontSize: 14, fontWeight: "900", color: "rgba(17,24,39,0.60)" },
});
